import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';

const Reports = ({ bets, onClose }) => {
  const [reportType, setReportType] = useState('performance');
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('sport');

  const getFilteredBets = useMemo(() => {
    let filtered = [...bets];
    if (dateRange === 'custom' && startDate && endDate) {
      filtered = filtered.filter(bet => {
        const betDate = new Date(bet.datePlaced || bet.date);
        return betDate >= new Date(startDate) && betDate <= new Date(endDate);
      });
    } else if (dateRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      switch (dateRange) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoff.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          cutoff.setFullYear(now.getFullYear() - 1);
          break;
      }
      filtered = filtered.filter(bet => {
        const betDate = new Date(bet.datePlaced || bet.date);
        return betDate >= cutoff;
      });
    }
    return filtered;
  }, [bets, dateRange, startDate, endDate]);

  const calculateStats = (betsList) => {
    const stats = {
      totalBets: betsList.length,
      wins: betsList.filter(b => b.status === 'won').length,
      losses: betsList.filter(b => b.status === 'lost').length,
      pushes: betsList.filter(b => b.status === 'push').length,
      pending: betsList.filter(b => !b.status || b.status === 'pending').length,
      totalUnitsStaked: betsList.reduce((sum, bet) => sum + parseFloat(bet.units || 1), 0),
      totalUnitsReturned: betsList
        .filter(b => b.status === 'won')
        .reduce((sum, bet) => sum + (parseFloat(bet.units || 1) * parseFloat(bet.odds)), 0),
      totalStaked: betsList.reduce((sum, bet) => sum + parseFloat(bet.stake || 0), 0),
      totalReturns: betsList
        .filter(b => b.status === 'won')
        .reduce((sum, bet) => sum + (parseFloat(bet.stake || 0) * parseFloat(bet.odds)), 0)
    };
    stats.unitsProfit = stats.totalUnitsReturned - stats.totalUnitsStaked;
    stats.profit = stats.totalReturns - stats.totalStaked;
    stats.roi = stats.totalUnitsStaked > 0 ? ((stats.unitsProfit / stats.totalUnitsStaked) * 100).toFixed(2) : 0;
    stats.winRate = (stats.wins + stats.losses) > 0
      ? ((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1)
      : 0;
    return stats;
  };

  const getGroupedData = useMemo(() => {
    const filtered = getFilteredBets;
    const grouped = {};
    filtered.forEach(bet => {
      const key = bet[groupBy] || 'Unknown';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(bet);
    });
    return Object.entries(grouped).map(([key, bets]) => ({
      name: key,
      ...calculateStats(bets),
      bets
    }));
  }, [getFilteredBets, groupBy]);

  const exportToExcel = () => {
    const data = getFilteredBets.map(bet => ({
      Date_Placed: new Date(bet.datePlaced || bet.date).toLocaleDateString(),
      Event_Date: bet.eventDateTime ? new Date(bet.eventDateTime).toLocaleDateString() : '',
      Sport: bet.sport,
      League: bet.league || '',
      Match: bet.match,
      Bet_Type: bet.betType || '',
      Selection: bet.bet,
      Odds: bet.odds,
      Closing_Line: bet.closingLine || '',
      Units: bet.units || 1,
      Stake: bet.stake || '',
      Status: bet.status || 'pending',
      Units_Return: bet.status === 'won' ? (parseFloat(bet.units || 1) * parseFloat(bet.odds)).toFixed(2) : 0,
      Units_Profit: bet.status === 'won'
        ? ((parseFloat(bet.units || 1) * parseFloat(bet.odds)) - parseFloat(bet.units || 1)).toFixed(2)
        : bet.status === 'lost' ? -parseFloat(bet.units || 1) : 0,
      Confidence: bet.confidence,
      Tipster: bet.tipster || '',
      Analysis: bet.analysis || '',
      Is_Multi: bet.isMultiLeg ? 'Yes' : 'No',
      Legs: bet.legs ? bet.legs.map(leg => `${leg.match}: ${leg.bet} @ ${leg.odds}`).join(' | ') : ''
    }));

    const headers = Object.keys(data[0] || {}).join(',');
    const rows = data.map(row =>
      Object.values(row).map(value =>
        typeof value === 'string' && value.includes(',')
          ? `"${value}"`
          : value
      ).join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bets_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const overallStats = calculateStats(getFilteredBets);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Reports & Analytics</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="performance">Performance Overview</option>
              <option value="breakdown">Breakdown Analysis</option>
              <option value="trends">Trends Over Time</option>
              <option value="details">Detailed Report</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="sport">Sport</option>
              <option value="league">League</option>
              <option value="confidence">Confidence</option>
              <option value="tipster">Tipster</option>
              <option value="status">Status</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={exportToExcel}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
            >
              Export to Excel
            </button>
          </div>
        </div>
        {dateRange === 'custom' && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="p-2 border border-gray-300 rounded-md"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="p-2 border border-gray-300 rounded-md"
            />
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Bets</p>
            <p className="text-2xl font-bold">{overallStats.totalBets}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Win Rate</p>
            <p className="text-2xl font-bold text-green-600">{overallStats.winRate}%</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Units</p>
            <p className="text-2xl font-bold">{overallStats.totalUnitsStaked.toFixed(1)}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Units P/L</p>
            <p className={`text-2xl font-bold ${overallStats.unitsProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {overallStats.unitsProfit >= 0 ? '+' : ''}{overallStats.unitsProfit.toFixed(2)}u
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">ROI</p>
            <p className={`text-2xl font-bold ${overallStats.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {overallStats.roi}%
            </p>
          </div>
        </div>
        {reportType === 'performance' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Performance by {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bets</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">W-L-P</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Win %</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units P/L</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ROI</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getGroupedData.map((group, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {group.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.totalBets}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {group.wins}-{group.losses}-{group.pushes}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.winRate}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {group.totalUnitsStaked.toFixed(1)}u
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${group.unitsProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {group.unitsProfit >= 0 ? '+' : ''}{group.unitsProfit.toFixed(2)}u
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${group.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {group.roi}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {reportType === 'breakdown' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Bet Type Distribution</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3">By Sport</h4>
                <div className="space-y-2">
                  {Object.entries(
                    getFilteredBets.reduce((acc, bet) => {
                      acc[bet.sport] = (acc[bet.sport] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([sport, count]) => (
                    <div key={sport} className="flex justify-between">
                      <span className="text-sm">{sport}</span>
                      <span className="text-sm font-medium">{count} ({((count / getFilteredBets.length) * 100).toFixed(1)}%)</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3">By Confidence</h4>
                <div className="space-y-2">
                  {['high', 'medium', 'low'].map(confidence => {
                    const count = getFilteredBets.filter(b => b.confidence === confidence).length;
                    return (
                      <div key={confidence} className="flex justify-between">
                        <span className="text-sm capitalize">{confidence}</span>
                        <span className="text-sm font-medium">
                          {count} ({((count / getFilteredBets.length) * 100).toFixed(1)}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        {reportType === 'details' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sport</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Match</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Odds</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stake</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">P/L</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredBets.slice(0, 50).map((bet, index) => {
                  const unitsProfit = bet.status === 'won'
                    ? ((parseFloat(bet.units || 1) * parseFloat(bet.odds)) - parseFloat(bet.units || 1))
                    : bet.status === 'lost' ? -parseFloat(bet.units || 1) : 0;
                  return (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(bet.datePlaced || bet.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bet.sport}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bet.match}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bet.bet}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bet.odds}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bet.units || 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{bet.stake ? `${bet.stake}` : '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          bet.status === 'won' ? 'bg-green-100 text-green-800' :
                          bet.status === 'lost' ? 'bg-red-100 text-red-800' :
                          bet.status === 'push' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {bet.status || 'pending'}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                        unitsProfit > 0 ? 'text-green-600' : unitsProfit < 0 ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {unitsProfit > 0 ? '+' : ''}{unitsProfit.toFixed(2)}u
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
